import { useUserRoleForCurrentWorkspace } from '@/features/roles';
import { authStore } from '@/services/auth/store';
import { getMasqBarVisibility, setMasqBarVisibility } from '@/utils/masq';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuGroup,
  DropdownMenuItem,
  Icon,
  Image,
  Paragraph,
  PlainButton,
  SegmentedControl,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import type { ColorSchemePreference, SegmentedControlOption } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useRef, useState } from 'react';
import { IconButton } from '../icon-button';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { BILLING_PATHS } from '@/features/billing';
import { useEssentialsOnboardingGate } from '@/features/onboarding';
import { useWorkspaceSetupStatus } from '@/features/setup-guide';
import { useThemeContext } from '@/context/theme';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { AccountState } from '@/services/auth/types';
import { selectHomeScreen } from '@/pages/workspaces/home/select-home-screen';
import heartPride from '@/assets/images/heart-pride.svg';
import notoJackOLantern from '@/assets/images/noto-jack-o-lantern.svg';
import * as styles from './index.css';
import { BRAND_EVERGREEN, BRAND_VERDANT, fireConfetti } from '@/components/feedback/confetti';
import confetti from 'canvas-confetti';
import { HealthLabel } from '@/features/workspace-health';
import { sessionsStore } from '@customerio/hydra-session-state';
import { useAnalytics } from '@/services/analytics';
import { UserRoleSection } from './user-role-section';
import { PersonalSettingsSection } from './personal-settings-section';

let heartShape: confetti.Shape | undefined;

/**
 * Built on the first click, then reused. `shapeFromPath` rasterizes the path on a canvas, and
 * at module scope that cost (~140ms of main-thread time under the perf harness's 4x CPU
 * throttle) was paid on every page load, since this dropdown is part of the authenticated
 * shell, for a shape that is only drawn when someone clicks the pride heart.
 */
function getHeartShape(): confetti.Shape {
  heartShape ??= confetti.shapeFromPath({
    path: 'M14.7,2.3c-1.6-1.6-4.3-1.6-6,0l-.8.8-.8-.8C5.6.6,2.9.6,1.3,2.3s-1.6,4.3,0,6l.8.8h0s6,6,6,6h0s0,0,0,0l6-6h0s.8-.8.8-.8c1.6-1.6,1.6-4.3,0-6Z',
  });
  return heartShape;
}

/** A theme selector value: a saved color scheme, or the Halloween skin on top of dark. */
type ThemeOption = ColorSchemePreference | 'halloween';

function toThemeOption(value: string): ThemeOption | null {
  switch (value) {
    case 'auto':
    case 'light':
    case 'dark':
    case 'halloween':
      return value;
    default:
      return null;
  }
}

const COLOR_SCHEME_OPTIONS: SegmentedControlOption[] = [
  { value: 'auto', label: 'System' },
  { value: 'light', label: 'Light', icon: 'light-mode' },
  { value: 'dark', label: 'Dark', icon: 'dark-mode' },
];

const THEME_OPTIONS_WITH_HALLOWEEN: SegmentedControlOption[] = [
  ...COLOR_SCHEME_OPTIONS,
  { value: 'halloween', label: 'Spooky', iconSrc: notoJackOLantern },
];

export function PersonDropdown() {
  const router = useRouter();
  const auth = useStore(authStore);
  const ref = useRef(null);
  const { hasPermission } = usePermissions();
  const userRole = useUserRoleForCurrentWorkspace();
  const currentWorkspace = useCurrentWorkspace();
  const setupStatus = useWorkspaceSetupStatus();
  const essentialsOnboarding = useEssentialsOnboardingGate();
  const sessions = useStore(sessionsStore);
  const showAdminMasqueradeOption =
    ((auth.account?.admin ?? false) && (auth.user?.can_masquerade ?? false)) || (auth.account?.is_masq ?? false);

  /**
   * Mirrors Ember's `adventures.setShowSetUpWorkspaceNavItem`: the item is visible for a loaded,
   * incomplete, non-demo, non-sample workspace. The shared status hook uses the same
   * `isWorkspaceSetupComplete` predicate, so this decision no longer depends on Ember writing
   * `sessionStorage.showSetUpWorkspaceNavItem`.
   */
  const homeScreen = selectHomeScreen({
    isBuilder: isBuilderPlan(auth.account),
    isSandbox: auth.account?.sandbox === true,
    isTrial: auth.account?.account_state === AccountState.Free,
    isSetupGuideComplete: setupStatus.setupGuide?.completed_at != null,
    isSetupGuideLoading: !setupStatus.isReady && setupStatus.isLoading,
    isSetupGuideSuccess: setupStatus.isReady,
    isEssentialsOnboardingEligible: essentialsOnboarding.isEligible,
    isEssentialsOnboardingComplete: essentialsOnboarding.isComplete,
    isEssentialsOnboardingLoading: essentialsOnboarding.isLoading,
  });

  const showSetUpWorkspaceNavItem =
    currentWorkspace?.id != null &&
    setupStatus.isReady &&
    setupStatus.setupGuide != null &&
    !setupStatus.isComplete &&
    !setupStatus.isDemoAccount &&
    !setupStatus.isSampleWorkspace &&
    homeScreen === 'workspace-setup';

  const setUpWorkspaceHref = showSetUpWorkspaceNavItem
    ? router.buildLocation({
        to: '/workspaces/$workspaceId/home',
        params: { workspaceId: currentWorkspace?.id },
      }).href
    : null;

  const [isShowingMasqBar, setIsShowingMasqBar] = useState(getMasqBarVisibility);
  const isShowingMasqBarRef = useRef(isShowingMasqBar);
  isShowingMasqBarRef.current = isShowingMasqBar;
  const { track } = useAnalytics();

  const onMasqueradeChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      event.preventDefault();

      const newValue = !isShowingMasqBarRef.current;
      setMasqBarVisibility(newValue);
      setIsShowingMasqBar(newValue);

      // If turning off masq bar, remove masq_account_id from URL
      if (!newValue) {
        const url = new URL(window.location.href);
        url.searchParams.delete('masq_account_id');
        window.location.href = url.toString();
      } else {
        // Reload the page, so that the Journeys UI picks up the local storage update
        window.location.reload();
      }
    },
    [setIsShowingMasqBar],
  );

  const onHeartClick = () => {
    fireConfetti(ref.current, {
      // These hex values match the colors from the heart svg, with the addition of our brand colors
      colors: [
        BRAND_EVERGREEN,
        BRAND_VERDANT,
        '#ea3041',
        '#f77e44',
        '#fbd733',
        '#5fbb5a',
        '#2799dd',
        '#1566bf',
        '#6d28ae',
      ],
      shapes: [getHeartShape()],
    });
  };

  const { colorScheme, isHalloween, setColorScheme, selectHalloween } = useThemeContext();
  const isHalloweenThemeEnabled = useFeatureFlag(AccountFeatureFlag.HalloweenTheme);
  const themeOption: ThemeOption = isHalloween ? 'halloween' : colorScheme;

  const onColorSchemeChange = useCallback(
    (value: string) => {
      const nextThemeOption = toThemeOption(value);

      if (nextThemeOption == null || nextThemeOption === themeOption) return;

      if (nextThemeOption === 'halloween') {
        selectHalloween();
      } else {
        setColorScheme(nextThemeOption);
      }
      void track('darkMode', 'colorSchemeChanged', {
        'Color Scheme': nextThemeOption,
        'Previous Color Scheme': themeOption,
      });
    },
    [themeOption, selectHalloween, setColorScheme, track],
  );

  return (
    <DropdownMenu initialFocus={-1}>
      {(context) => (
        <IconButton
          label="Menu"
          icon="manage"
          size="md"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        />
      )}
      <DropdownMenuContent>
        <UserRoleSection userRole={userRole} setUpWorkspaceHref={setUpWorkspaceHref} />

        <PersonalSettingsSection />
        <DropdownMenuGroup>
          <DropdownMenuItem href="/settings">Account settings</DropdownMenuItem>
          {auth.account?.partner_account === true && hasPermission('account/edit') === true ? (
            <DropdownMenuItem href="/settings/partner">Partner settings</DropdownMenuItem>
          ) : null}
          {hasPermission('account/team_members/edit') === true ? (
            <DropdownMenuItem href="/settings/team/add">Invite teammate</DropdownMenuItem>
          ) : null}
          {hasPermission('account/team_members/edit') === true ? (
            <DropdownMenuItem href="/settings/team/invite-guest">Invite guest</DropdownMenuItem>
          ) : null}
          <DropdownMenuItem href="/settings/privacy">Privacy &amp; data</DropdownMenuItem>
          {hasPermission('account/support_access/view') === true ? (
            <DropdownMenuItem href="/settings/ai">AI &amp; MCP</DropdownMenuItem>
          ) : null}
          {hasPermission('account/billing/view') === true ? (
            <DropdownMenuItem href={BILLING_PATHS.overview}>Plans &amp; billing</DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            href={
              router.buildLocation({
                to: '/workspaces/$workspaceId/settings',
                params: { workspaceId: currentWorkspace?.id ?? 'last' },
              }).href
            }
          >
            Workspace settings
          </DropdownMenuItem>
          <DropdownMenuItem
            href={
              router.buildLocation({
                to: '/workspaces/$workspaceId/health',
                params: { workspaceId: currentWorkspace?.id },
              }).href
            }
          >
            <Box display="flex" alignItems="center" gap="200" justifyContent="space-between">
              <Text>Workspace performance</Text> <HealthLabel />
            </Box>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuItem>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <SegmentedControl
                flexGrow="1"
                size="sm"
                label="Theme"
                value={themeOption}
                onChange={onColorSchemeChange}
                options={isHalloweenThemeEnabled ? THEME_OPTIONS_WITH_HALLOWEEN : COLOR_SCHEME_OPTIONS}
              />
            </Box>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {showAdminMasqueradeOption ? (
          <DropdownMenuGroup>
            {/* oxlint-disable pluma/enforce-pluma-elements -- menu item acts as the label surface for the toggle. */}
            <DropdownMenuItem
              as="label"
              id="toggle-admin-masq-label"
              htmlFor="toggle-admin-masq"
              shouldCloseOnClick={false}
              // noop onClick to make the whole item styled as interactive
              onClick={() => {}}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Text>Admin masquerade</Text>
                <Toggle
                  size="sm"
                  onChange={(event) => onMasqueradeChange(event)}
                  ariaLabelledby="toggle-admin-masq-label"
                  id="toggle-admin-masq"
                  isChecked={isShowingMasqBar}
                />
              </Box>
            </DropdownMenuItem>
            {/* oxlint-enable pluma/enforce-pluma-elements */}
          </DropdownMenuGroup>
        ) : null}
        <DropdownMenuGroup>
          <DropdownMenuItem href="/logout" isCritical={true}>
            {sessions.length > 1 ? 'Log out from all accounts' : 'Log out'}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuFooter className={styles.dropdownFooter}>
          <Paragraph sizeVariant="xs" display="flex" flexWrap="wrap" alignItems="center" gap="050">
            <span id="made-with">Made with</span>
            <PlainButton
              aria-labelledby="made-with heart-love around-the-world"
              ref={ref}
              className={styles.heartContainer}
              onClick={onHeartClick}
            >
              <Icon className={styles.heartContainerHeartPlain} name="heart" id="heart-love" aria-label="love" />
              <Image className={styles.heartContainerHeartPride} src={heartPride} aria-label="love" />
            </PlainButton>
            <span id="around-the-world">around the world.</span>
          </Paragraph>
        </DropdownMenuFooter>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
